import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Button,
  Typography,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  TableChart as TableIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';

const FIELD_TYPES = [
  'string',
  'integer',
  'number',
  'boolean',
  'array',
  'object',
  'date',
  'datetime',
  'email',
  'uuid',
];

const emptyRow = () => ({ name: '', type: 'string', required: false, description: '' });

/**
 * SchemaBuilder — visual table editor for JSON schemas with Raw JSON toggle.
 *
 * Props:
 *   value     — object (JSON schema / key-value map) or null
 *   onChange  — (newValue: object | null) => void
 *   label     — section heading (optional)
 *   height    — Monaco editor height in px (default 200)
 */
const SchemaBuilder = ({ value, onChange, label, height = 200 }) => {
  const [mode, setMode] = useState('visual'); // 'visual' | 'json'
  const [rows, setRows] = useState([emptyRow()]);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');

  // ── Sync value → rows (on mount / external value change) ──
  useEffect(() => {
    if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
      setRows([emptyRow()]);
      setJsonText(value ? JSON.stringify(value, null, 2) : '');
      return;
    }
    const newRows = Object.entries(value).map(([key, def]) => {
      if (typeof def === 'object' && def !== null) {
        return {
          name: key,
          type: def.type || 'string',
          required: !!def.required,
          description: def.description || '',
        };
      }
      // Simple key:value (non-object leaf)
      return { name: key, type: String(def), required: false, description: '' };
    });
    setRows(newRows.length > 0 ? newRows : [emptyRow()]);
    setJsonText(JSON.stringify(value, null, 2));
  }, [value]);

  // ── Rows → JSON object ────────────────────────────────────
  const rowsToObject = useCallback((r) => {
    const obj = {};
    r.forEach((row) => {
      if (!row.name.trim()) return;
      obj[row.name.trim()] = {
        type: row.type,
        required: row.required,
        ...(row.description ? { description: row.description } : {}),
      };
    });
    return Object.keys(obj).length > 0 ? obj : null;
  }, []);

  // ── Visual row change handler ─────────────────────────────
  const handleRowChange = (index, field, val) => {
    const updated = rows.map((r, i) => (i === index ? { ...r, [field]: val } : r));
    setRows(updated);
    const obj = rowsToObject(updated);
    onChange(obj);
    setJsonText(obj ? JSON.stringify(obj, null, 2) : '');
    setJsonError('');
  };

  const addRow = () => {
    const updated = [...rows, emptyRow()];
    setRows(updated);
  };

  const removeRow = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    const final = updated.length > 0 ? updated : [emptyRow()];
    setRows(final);
    const obj = rowsToObject(final);
    onChange(obj);
    setJsonText(obj ? JSON.stringify(obj, null, 2) : '');
  };

  // ── JSON text change handler ──────────────────────────────
  const handleJsonChange = (text) => {
    setJsonText(text || '');
    if (!text || !text.trim()) {
      setJsonError('');
      onChange(null);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      setJsonError('');
      onChange(parsed);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  // ── Mode switch: sync data between modes ──────────────────
  const handleModeChange = (_, newMode) => {
    if (!newMode) return;
    if (newMode === 'json') {
      // rows → json text
      const obj = rowsToObject(rows);
      setJsonText(obj ? JSON.stringify(obj, null, 2) : '');
    }
    // json → visual: re-parse from current value (useEffect handles it)
    setMode(newMode);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        {label && (
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
        )}
        <ToggleButtonGroup size="small" value={mode} exclusive onChange={handleModeChange}>
          <ToggleButton value="visual">
            <Tooltip title="Visual Editor"><TableIcon fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="json">
            <Tooltip title="Raw JSON"><CodeIcon fontSize="small" /></Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === 'visual' ? (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600, width: '25%' }}>Field Name</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '15%' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '8%', textAlign: 'center' }}>Req</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    <TextField
                      size="small"
                      variant="standard"
                      fullWidth
                      placeholder="field_name"
                      value={row.name}
                      onChange={(e) => handleRowChange(i, 'name', e.target.value)}
                      InputProps={{ disableUnderline: true, sx: { fontSize: 13, fontFamily: 'monospace' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    <Select
                      size="small"
                      variant="standard"
                      fullWidth
                      value={row.type}
                      onChange={(e) => handleRowChange(i, 'type', e.target.value)}
                      disableUnderline
                      sx={{ fontSize: 13 }}
                    >
                      {FIELD_TYPES.map((t) => (
                        <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, textAlign: 'center' }}>
                    <Checkbox
                      size="small"
                      checked={row.required}
                      onChange={(e) => handleRowChange(i, 'required', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    <TextField
                      size="small"
                      variant="standard"
                      fullWidth
                      placeholder="Description..."
                      value={row.description}
                      onChange={(e) => handleRowChange(i, 'description', e.target.value)}
                      InputProps={{ disableUnderline: true, sx: { fontSize: 13 } }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 0 }}>
                    <IconButton size="small" onClick={() => removeRow(i)} tabIndex={-1}>
                      <DeleteIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ px: 1, py: 0.5 }}>
            <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ textTransform: 'none', fontSize: 12 }}>
              Add Field
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ border: '1px solid', borderColor: jsonError ? 'error.main' : 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height={height}
            language="json"
            theme="vs-dark"
            value={jsonText}
            onChange={handleJsonChange}
            options={{
              minimap: { enabled: false },
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              fontSize: 13,
              tabSize: 2,
              automaticLayout: true,
              wordWrap: 'on',
            }}
          />
          {jsonError && (
            <Typography variant="caption" color="error" sx={{ px: 1, py: 0.5, display: 'block' }}>
              {jsonError}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SchemaBuilder;
